import { fDateTime } from '../../../utils/formatTime';

export const ADJUSTMENT_TYPE_OPTIONS = [
  { value: 'ledger_adjustment', label: 'Ledger Adjustment' },
  { value: 'wallet_snapshot_correction', label: 'Wallet Snapshot Correction' },
];

export const TARGET_TYPE_OPTIONS = [{ value: 'user', label: 'User' }];

export const WALLET_TYPE_OPTIONS = [
  { value: 'fiat', label: 'Fiat Wallet' },
  { value: 'usdt', label: 'USDT Wallet' },
];

export const DIRECTION_OPTIONS = [
  { value: 'credit', label: 'Credit' },
  { value: 'debit', label: 'Debit' },
];

export const STATUS_TABS = [
  { value: 'all', label: 'All Requests' },
  { value: 'pending_approval', label: 'Pending Approval' },
  { value: 'awaiting_execution', label: 'Awaiting Execution' },
  { value: 'executing', label: 'Executing' },
  { value: 'executed', label: 'Executed' },
  { value: 'rejected_or_cancelled', label: 'Rejected / Cancelled' },
];

export const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

export const FILE_ACCEPT_ATTRIBUTE = '.pdf,.jpg,.jpeg,.png,.webp,.doc,.docx';

export function getAssetCodeForWalletType(walletType) {
  if (walletType === 'usdt') {
    return 'USDT';
  }

  return 'NGN';
}

export function normalizeDecimalInput(value) {
  if (value === undefined || value === null) {
    return '';
  }

  return `${value}`.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');
}

export function formatDecimalForDisplay(value, currency = '') {
  if (value === undefined || value === null || value === '') {
    return 'N/A';
  }

  const numeric = Number.parseFloat(value);
  if (Number.isNaN(numeric)) {
    return `${currency ? `${currency} ` : ''}${value}`;
  }

  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(numeric);

  return currency ? `${currency} ${formatted}` : formatted;
}

export function formatReasonCodeLabel(reason) {
  if (!reason) {
    return 'Unknown reason code';
  }

  const scope = reason.direction_scope ? ` (${reason.direction_scope})` : '';
  return `${reason.label || reason.reason_code}${scope}`;
}

export function isReasonCodeActive(reason) {
  return Number(reason?.is_active) === 1;
}

export function reasonCodeRequiresReference(reason) {
  return Number(reason?.requires_reference) === 1;
}

export function reasonCodeRequiresAttachment(reason) {
  return Number(reason?.requires_attachment) === 1;
}

export function getAllowedActions(status) {
  if (status === 'pending_approval') {
    return ['approve', 'reject'];
  }

  if (status === 'awaiting_execution') {
    return ['cancel', 'execute'];
  }

  return [];
}

export function formatAdjustmentAmount(request) {
  if (!request) {
    return 'N/A';
  }

  if (request.adjustment_type === 'wallet_snapshot_correction') {
    return 'Snapshot correction';
  }

  return formatDecimalForDisplay(request.amount, request.asset_code || '');
}

export function formatAdjustmentType(value) {
  return value ? value.replace(/_/g, ' ') : 'N/A';
}

export function formatValue(value, fallback = 'N/A') {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : fallback;
  }

  return `${value}`;
}

export function matchesStatusFilter(row, filter) {
  if (filter === 'all') {
    return true;
  }

  if (filter === 'rejected_or_cancelled') {
    return row.status === 'rejected' || row.status === 'cancelled';
  }

  return row.status === filter;
}

export function parseEventPayload(payload) {
  if (!payload || typeof payload !== 'string') {
    return null;
  }

  try {
    return JSON.parse(payload);
  } catch (error) {
    return null;
  }
}

export function buildEventTimelineItems(events = []) {
  return events.map((event, index) => {
    const parsed = parseEventPayload(event.payload);
    const payloadSummary = parsed
      ? Object.entries(parsed)
          .slice(0, 4)
          .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
          .join(' | ')
      : 'No parsed payload details available.';

    return {
      id: `${event.id || index}`,
      title: formatAdjustmentType(event.event_type),
      description: `Staff: ${formatValue(event.staff_id)} | ${payloadSummary}`,
      time: event.created_at ? fDateTime(event.created_at) : 'N/A',
      color:
        event.event_type === 'approved' || event.event_type === 'executed'
          ? 'success'
          : event.event_type === 'rejected' || event.event_type === 'cancelled'
            ? 'error'
            : 'primary',
    };
  });
}

export function buildCreatePayload(form, files = []) {
  const payload = new FormData();

  Object.entries(form).forEach(([key, value]) => {
    if (value !== undefined && value !== null && `${value}`.trim() !== '') {
      payload.append(key, `${value}`);
    }
  });

  files.forEach((file) => {
    payload.append('userfiles[]', file);
  });

  return payload;
}

export function getRequestSummaryForDialog(request) {
  if (!request) {
    return '';
  }

  return [
    `Request ${formatValue(request.request_id)}`,
    `${formatAdjustmentType(request.adjustment_type)}`,
    `${formatValue(request.direction)}`,
    `${formatValue(request.reason_code)}`,
    `${formatAdjustmentAmount(request)}`,
  ].join(' • ');
}
